import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, CircularProgress, Alert, IconButton, Tooltip,
  InputAdornment, TableContainer, Stack,
} from '@mui/material';
import {
  Search, Add, EmojiEvents, Redeem, History, PersonAdd, Refresh,
} from '@mui/icons-material';
import {
  fetchCustomers, createCustomer, searchCustomers, addLoyaltyPoints,
  redeemLoyaltyPoints, getCustomerHistory,
} from '../../api/api';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function ManageCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);

  /* New customer dialog */
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', phone: '', email: '' });
  const [newLoading, setNewLoading] = useState(false);
  const [newError, setNewError] = useState('');

  /* Add points dialog */
  const [addPtsOpen, setAddPtsOpen] = useState(false);
  const [addPtsTarget, setAddPtsTarget] = useState(null);
  const [addPtsAmount, setAddPtsAmount] = useState('');
  const [addPtsReason, setAddPtsReason] = useState('');
  const [addPtsLoading, setAddPtsLoading] = useState(false);
  const [addPtsError, setAddPtsError] = useState('');

  /* Redeem dialog */
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [redeemPts, setRedeemPts] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');

  /* History dialog */
  const [histOpen, setHistOpen] = useState(false);
  const [histTarget, setHistTarget] = useState(null);
  const [histData, setHistData] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchCustomers();
      setCustomers(res.data?.customers || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQ.trim()) { loadCustomers(); return; }
    try {
      setSearching(true);
      const res = await searchCustomers(searchQ.trim());
      setCustomers(res.data?.customers || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  /* new customer */
  const handleCreateCustomer = async () => {
    if (!newForm.name.trim() || !newForm.phone.trim()) { setNewError('Name and phone are required.'); return; }
    try {
      setNewLoading(true);
      setNewError('');
      await createCustomer(newForm);
      setNewOpen(false);
      setNewForm({ name: '', phone: '', email: '' });
      await loadCustomers();
    } catch (err) {
      setNewError(err.response?.data?.message || 'Failed to create customer.');
    } finally {
      setNewLoading(false);
    }
  };

  /* add points */
  const openAddPts = (c) => { setAddPtsTarget(c); setAddPtsAmount(''); setAddPtsReason(''); setAddPtsError(''); setAddPtsOpen(true); };
  const handleAddPoints = async () => {
    if (!addPtsAmount || Number(addPtsAmount) <= 0) { setAddPtsError('Enter a valid points amount.'); return; }
    try {
      setAddPtsLoading(true);
      setAddPtsError('');
      await addLoyaltyPoints(addPtsTarget._id, { points: Number(addPtsAmount), reason: addPtsReason });
      setAddPtsOpen(false);
      await loadCustomers();
    } catch (err) {
      setAddPtsError(err.response?.data?.message || 'Failed to add points.');
    } finally {
      setAddPtsLoading(false);
    }
  };

  /* redeem */
  const openRedeem = (c) => { setRedeemTarget(c); setRedeemPts(''); setRedeemError(''); setRedeemOpen(true); };
  const handleRedeem = async () => {
    const pts = Number(redeemPts);
    if (!pts || pts <= 0) { setRedeemError('Enter a valid points amount.'); return; }
    if (pts > (redeemTarget.loyaltyPoints || 0)) { setRedeemError('Not enough points.'); return; }
    try {
      setRedeemLoading(true);
      setRedeemError('');
      await redeemLoyaltyPoints(redeemTarget._id, { points: pts });
      setRedeemOpen(false);
      await loadCustomers();
    } catch (err) {
      setRedeemError(err.response?.data?.message || 'Failed to redeem points.');
    } finally {
      setRedeemLoading(false);
    }
  };

  /* history */
  const openHistory = async (c) => {
    setHistTarget(c);
    setHistData([]);
    setHistOpen(true);
    setHistLoading(true);
    try {
      const res = await getCustomerHistory(c._id);
      setHistData(res.data?.history || res.data || []);
    } catch {
      setHistData([]);
    } finally {
      setHistLoading(false);
    }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1e293b">Loyalty Customers</Typography>
          <Typography variant="body2" color="text.secondary">Manage walk-in customers and loyalty points</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadCustomers} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => { setNewForm({ name: '', phone: '', email: '' }); setNewError(''); setNewOpen(true); }}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}
          >
            New Customer
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2, display: 'flex', gap: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <TextField
          size="small"
          placeholder="Search by name or phone..."
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <Button variant="outlined" onClick={handleSearch} disabled={searching} sx={{ borderRadius: 2 }}>
          {searching ? <CircularProgress size={18} /> : 'Search'}
        </Button>
        {searchQ && (
          <Button variant="text" onClick={() => { setSearchQ(''); loadCustomers(); }} sx={{ borderRadius: 2 }}>Clear</Button>
        )}
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Name', 'Phone', 'Email', 'Loyalty Points', 'Total Spent', 'Visits', 'Last Visit', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>No customers found.</TableCell>
                </TableRow>
              ) : customers.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell><Typography fontWeight={600}>{c.name}</Typography></TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell><Typography variant="caption">{c.email || '—'}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      label={`${c.loyaltyPoints || 0} pts`}
                      color="warning"
                      size="small"
                      icon={<EmojiEvents sx={{ fontSize: 14 }} />}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>{KES(c.totalSpent || c.totalPurchases || 0)}</TableCell>
                  <TableCell>{c.visitCount ?? c.totalOrders ?? 0}</TableCell>
                  <TableCell>{fmt(c.lastVisit || c.lastPurchase || c.updatedAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Add points">
                        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openAddPts(c)}
                          sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1, background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}>
                          Add Pts
                        </Button>
                      </Tooltip>
                      <Tooltip title="Redeem points">
                        <Button size="small" variant="outlined" startIcon={<Redeem />} onClick={() => openRedeem(c)}
                          sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1 }} disabled={(c.loyaltyPoints || 0) === 0}>
                          Redeem
                        </Button>
                      </Tooltip>
                      <Tooltip title="View history">
                        <IconButton size="small" onClick={() => openHistory(c)}>
                          <History fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── New Customer Dialog ─── */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>New Customer</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Full Name *" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} fullWidth size="small" />
            <TextField label="Phone Number *" value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} fullWidth size="small" />
            <TextField label="Email (optional)" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} fullWidth size="small" />
            {newError && <Alert severity="error">{newError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCustomer} disabled={newLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
            {newLoading ? <CircularProgress size={18} /> : 'Create Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Points Dialog ─── */}
      <Dialog open={addPtsOpen} onClose={() => setAddPtsOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Add Points: {addPtsTarget?.name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Points to Add" type="number" value={addPtsAmount} onChange={(e) => setAddPtsAmount(e.target.value)} fullWidth size="small" inputProps={{ min: 1 }} />
            <TextField label="Reason" value={addPtsReason} onChange={(e) => setAddPtsReason(e.target.value)} fullWidth size="small" placeholder="e.g. Bonus, Promotion, Correction" />
            {addPtsError && <Alert severity="error">{addPtsError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddPtsOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPoints} disabled={addPtsLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}>
            {addPtsLoading ? <CircularProgress size={18} /> : 'Add Points'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Redeem Dialog ─── */}
      <Dialog open={redeemOpen} onClose={() => setRedeemOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Redeem Points: {redeemTarget?.name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ p: 1.5, bgcolor: '#fef9c3', borderRadius: 2, border: '1px solid #fde68a' }}>
              <Typography variant="body2" fontWeight={700}>Available: {redeemTarget?.loyaltyPoints || 0} pts</Typography>
              <Typography variant="caption" color="text.secondary">Value: {KES((redeemTarget?.loyaltyPoints || 0) * 0.5)} (KES 0.50 per point)</Typography>
            </Box>
            <TextField
              label="Points to Redeem"
              type="number"
              value={redeemPts}
              onChange={(e) => setRedeemPts(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: redeemTarget?.loyaltyPoints || 0 }}
              helperText={redeemPts ? `Value: ${KES(Number(redeemPts) * 0.5)}` : ''}
            />
            {redeemError && <Alert severity="error">{redeemError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRedeemOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleRedeem} disabled={redeemLoading} sx={{ borderRadius: 2 }}>
            {redeemLoading ? <CircularProgress size={18} /> : 'Redeem Points'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── History Dialog ─── */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Purchase History: {histTarget?.name}</DialogTitle>
        <DialogContent>
          {histLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : histData.length === 0 ? (
            <Alert severity="info">No history found.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Date', 'Sale #', 'Items', 'Total', 'Payment', 'Points Earned'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histData.map((tx, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{fmt(tx.createdAt || tx.date)}</TableCell>
                      <TableCell><Typography variant="caption">{tx.saleNumber || tx._id?.slice(-8).toUpperCase() || '—'}</Typography></TableCell>
                      <TableCell>{tx.itemCount ?? tx.items?.length ?? 0}</TableCell>
                      <TableCell><Typography fontWeight={700}>{KES(tx.total)}</Typography></TableCell>
                      <TableCell><Chip label={(tx.paymentMethod || '—').toUpperCase()} size="small" /></TableCell>
                      <TableCell>{tx.pointsEarned ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistOpen(false)} sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

